import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useIdleTimer } from "react-idle-timer";
import Modal from "../common/Modal";
import config from "../../config/config";
import "../../custom.css";

/**
 * Idle detection with a warning dialog (similar to @ng-idle/core).
 * `onPrompt` runs after `idleMsBeforeWarning` with no activity; session ends after `warningGraceMs` unless the user continues.
 */
export default function IdleSessionWatcher({
  enabled = true,
  idleMsBeforeWarning = config.idlePromptAfterMs,
  warningGraceMs = config.idlePromptGraceMs,
  onTimeout,
  /** Immediate sign-out from the dialog (same as manual logout). Falls back to `onTimeout` if omitted. */
  onSignOut,
  timerName = "session",
}) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [remainingSec, setRemainingSec] = useState(0);

  const grace = Math.max(1000, Number(warningGraceMs) || config.idlePromptGraceMs);
  const idleBefore = Math.max(1000, Number(idleMsBeforeWarning) || config.idlePromptAfterMs);
  const idleMinutesForCopy = Math.max(1, Math.round(idleBefore / 60_000));
  const { timeout, promptBeforeIdle } = useMemo(() => {
    const t = idleBefore + grace;
    const p = Math.min(grace, Math.max(1000, t - 1000));
    return { timeout: t, promptBeforeIdle: p };
  }, [idleBefore, grace]);

  const handleIdle = useCallback(async () => {
    setShowPrompt(false);
    try {
      await onTimeout?.();
    } catch (e) {
      console.error("Idle timeout handler error:", e);
    }
  }, [onTimeout]);

  const handleActive = useCallback(() => {
    setShowPrompt(false);
  }, []);

  const handlePrompt = useCallback(() => {
    setShowPrompt(true);
  }, []);

  const { reset, getRemainingTime } = useIdleTimer({
    timeout,
    promptBeforeIdle,
    onPrompt: handlePrompt,
    onIdle: handleIdle,
    onActive: handleActive,
    disabled: !enabled,
    name: timerName,
    startOnMount: true,
    element: typeof document !== "undefined" ? document : undefined,
  });

  useEffect(() => {
    if (!showPrompt) return;
    const tick = () => {
      const ms = Math.max(0, getRemainingTime());
      setRemainingSec(Math.ceil(ms / 1000));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [showPrompt, getRemainingTime]);

  const staySignedIn = useCallback(() => {
    reset();
    setShowPrompt(false);
  }, [reset]);

  const handleSignOut = useCallback(async () => {
    setShowPrompt(false);
    reset();
    const fn = onSignOut || onTimeout;
    try {
      await fn?.();
    } catch (e) {
      console.error("Idle sign-out handler error:", e);
    }
  }, [onSignOut, onTimeout, reset]);

  if (!enabled || !showPrompt || typeof document === "undefined") return null;

  return createPortal(
    <Modal
      title="Idle Notice"
      size="md"
      overlayClassName="idle-session-modal"
      onClose={staySignedIn}
    >
      <div className="modelspace" role="document">
        <p className="heading-b" style={{ marginBottom: 12, lineHeight: 1.5 }}>
          Your session has been idle for about <strong>{idleMinutesForCopy}</strong> minute
          {idleMinutesForCopy === 1 ? "" : "s"}. You will be automatically signed out in{" "}
          <strong>{remainingSec}</strong> second{remainingSec === 1 ? "" : "s"} unless you continue.
        </p>
        <div className="frm-btn idle-session-actions">
          <button type="button" className="submit-btn btn-bg inpt-btn btn-discard" onClick={handleSignOut}>
            Sign out
          </button>
          <button type="button" className="submit-btn btn-bg inpt-btn" onClick={staySignedIn}>
            Stay signed in
          </button>
        </div>
      </div>
    </Modal>,
    document.body
  );
}
